import forge from 'apis/forge';
import * as api from 'dragonmouse-shared/api/forge';
import { Point } from 'dragonmouse-shared/geometry';
import { DeviceType, FrameStatus, FrameType, Moonshot } from 'dragonmouse-shared/interfaces';
import { toast } from 'react-toastify';
import { createFrames } from 'redux/db/util';
import { displayConfirmModal } from 'redux/mode/util';
import { v4 as uuid } from 'uuid';
import { openFigmaAuthPopup } from 'utils';

export const getClipboardItems = async (): Promise<ClipboardItem[]> => {
	try {
		const clipboardItems = await navigator.clipboard.read();
		return clipboardItems;
	} catch (error) {
		if (error instanceof Error) {
			if (error.name === 'NotAllowedError') {
				if (error.message.includes('Document is not focused')) {
					toast.warn('Browser is not focused. Please click the canvas to enable paste.');
				} else if (error.message.includes('Read permission denied')) {
					toast.warn('Clipboard access denied. Please consult your browser settings.');
				} else {
					toast.warn(
						'Cannot access clipboard. Please consult your browser settings or contact support.'
					);
				}
			}
		}
		return [];
	}
};

export const hasPastableItems = async (clipboardItems: ClipboardItem[]) => {
	if (clipboardItems.some((item) => item.types.includes('image/png'))) return true;

	for (const item of clipboardItems) {
		if (!item.types.includes('text/plain')) continue;
		const text = await item.getType('text/plain');
		const textContent = await text.text();
		if (getFigmaLink(textContent)) return true;
		if (getFrameIdsFromText(textContent).length > 0) return true;
		if (getMoonshotClipboardFromText(textContent)) return true;
	}
	return false;
};

// make a new frame with the provided item's image data on the scene
// at the provided point "scene" which is in scene coordinates
// item must be of type `'image/png'
export const pasteImageAsFrame = async (item: ClipboardItem, sceneId: string, scene: Point) => {
	const blob = await item.getType('image/png');
	if (!blob) throw new Error('Clipboard item is not an image');

	const url = URL.createObjectURL(blob);

	const frameId = uuid();

	// use the Image constructor to get the width and height of the image
	// and while we're at it, create the frame
	const img = new Image();
	img.onload = async () => {
		await forge.createFrame(
			{
				id: frameId,
				sceneId,
				title: `Imported Image`,
				x: Math.round(scene.x),
				y: Math.round(scene.y),
				width: Math.round(img.width),
				height: Math.round(img.height),
				metadata: {
					type: FrameType.IMPORT,
					status: FrameStatus.PROCESSING,
					statusMessage: 'preparing image import',
					displayLabels: {
						'original size': `${Math.round(img.width)}x${Math.round(img.height)}`,
					},
				},
			},
			true
		);

		// Use FileReader to convert Blob to base64. It's kind a shame that we have
		// to use this along with an Image, but it seems that the Image constructor
		// can't get the data and the FileReader doesn't know dimensions.
		const reader = new FileReader();
		reader.onload = async () => {
			// reader.result will look like "data:image/png;base64,iVBORw..."
			const fullBase64 = reader.result as string;

			// Strip off the 'data:image/png;base64,' part to get the raw base64
			const base64Only = fullBase64.split(',')[1];

			const req: api.ImportImageRequest = {
				targetFrameId: frameId,
				imageData: base64Only,
			};
			const response = await forge.importImage.withToast()(req);
			if (!response.success) {
				throw new Error('Failed to import image');
			}
		};
		reader.readAsDataURL(blob); // this triggers the above onload

		URL.revokeObjectURL(url); // Clean up the temporary URL
	};
	img.src = url; // this triggers the above onload
};

export const getFigmaLink = (link: string): string | null => {
	if (link.includes('https://www.figma.com/design')) {
		return link;
	}
	return null;
};

export const getFrameIdsFromText = (text: string): string[] => {
	// Match all occurrences of frame links and extract their IDs
	// Supports both production and local development URLs
	const linkRegex = /https?:\/\/[^\/]+\/project\/[^\/]+\/[^?]+\?frameId=([a-zA-Z0-9\-]+)/g;
	const frameIds: string[] = [];
	let match;
	while ((match = linkRegex.exec(text)) !== null) {
		frameIds.push(match[1]);
	}
	return frameIds;
};

export const getMoonshotClipboardFromText = (text: string): Moonshot | null => {
	try {
		const data = JSON.parse(text);
		const SUPPORTED_VERSIONS = ['moonshot:v0.0.1', 'moonshot:v0.0.2'];
		if (
			data.code &&
			data.title &&
			data.viewport &&
			typeof data.viewport === 'object' &&
			typeof data.viewport.width === 'number' &&
			typeof data.viewport.height === 'number' &&
			data.fullPage &&
			typeof data.fullPage === 'object' &&
			typeof data.fullPage.width === 'number' &&
			typeof data.fullPage.height === 'number' &&
			data.siteMap &&
			typeof data.siteMap === 'object' &&
			data.url &&
			typeof data.url === 'string' &&
			SUPPORTED_VERSIONS.includes(data.code)
		) {
			return data;
		}
		return null;
	} catch (error) {
		return null;
	}
};

export const pasteFigmaLinkAsFrame = async (link: string, scene: Point) => {
	const authResponse = await forge.authFigmaStatus.withToast()();
	if (!authResponse.loggedIn) {
		displayConfirmModal({
			title: 'Login to Figma',
			description: 'You have to log in to Figma to paste a Figma link. Continue?',
			onCancel: () => {},
			onSubmit: async () => {
				const success = await openFigmaAuthPopup();
				if (!success) return;
				// retry the paste after successful auth
				pasteFigmaLinkAsFrame(link, scene);
			},
		});
		return;
	}

	const device = {
		type: DeviceType.CUSTOM,
		width: 256,
		height: 512,
	};
	const targetFrameIds: string[] = await createFrames(
		1,
		device,
		FrameType.IMPORT,
		'figma-import - ',
		{
			'Figma link': link,
		},
		[],
		null,
		{
			x: Math.round(scene.x),
			y: Math.round(scene.y),
			width: device.width,
			height: device.height,
		}
	);

	const req: api.ImportFigmaRequest = {
		targetFrameIds: targetFrameIds,
		figmaLink: link,
	};
	const response = await forge.importFigma.withToast()(req);
	if (!response.success) {
		throw new Error('Failed to import Figma link');
	}
};

export const pasteMoonshotAsFrame = async (moonshot: Moonshot, sceneId: string, scene: Point) => {
	const frameId = uuid();

	await forge.createFrame(
		{
			id: frameId,
			sceneId,
			title: moonshot.title || 'Imported Moonshot',
			x: Math.round(scene.x),
			y: Math.round(scene.y),
			width: moonshot.viewport.width,
			height: moonshot.viewport.height,
			metadata: {
				type: FrameType.IMPORT_SITE,
				status: FrameStatus.PROCESSING,
				statusMessage: 'preparing moonshot import',
				displayLabels: {
					URL: moonshot.url,
				},
			},
		},
		true
	);

	const req: api.ImportMoonshotRequest = {
		targetFrameId: frameId,
		moonshot,
	};
	const response = await forge.importMoonshot.withToast()(req);
	if (!response.success) {
		throw new Error('Failed to import Moonshot');
	}
};

export const putTextOnClipboard = async (text: string) => {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return;
		} catch (error) {
			console.warn('navigator.clipboard.writeText failed, falling back to execCommand', error);
		}
	}

	const textarea = document.createElement('textarea');
	textarea.value = text;
	textarea.setAttribute('readonly', 'readonly');
	textarea.style.position = 'absolute';
	textarea.style.left = '-9999px';
	textarea.style.opacity = '0';
	document.body.appendChild(textarea);

	const selection = document.getSelection();
	const storedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

	textarea.select();
	textarea.setSelectionRange(0, text.length);

	let succeeded = false;

	try {
		succeeded = document.execCommand('copy');
	} catch (error) {
		console.error('document.execCommand copy failed', error);
		succeeded = false;
	} finally {
		document.body.removeChild(textarea);
		if (storedRange && selection) {
			selection.removeAllRanges();
			selection.addRange(storedRange);
		}
	}

	if (!succeeded) {
		throw new Error('Copy command failed');
	}
};
